/**
 * Base URL for the devfoliofunctions Cloud Function (BigQuery departments/employees).
 * Set REACT_APP_DEVFOLIO_API_URL in .env to override.
 * - Development: defaults to Functions emulator (when using npm start + emulator).
 * - Production: defaults to deployed function (cynet-labs).
 *
 * Every request carries the signed-in user's Firebase ID token; the function
 * rejects calls without one (see server/middleware/requireAuth.js).
 */
import { auth } from "../firebase";

const DEFAULT_DEV =
  "http://127.0.0.1:5001/cynet-labs/us-central1/devfoliofunctions";
const DEFAULT_PROD =
  "https://us-central1-cynet-labs.cloudfunctions.net/devfoliofunctions";
const DEVFOLIO_API_BASE =
  process.env.REACT_APP_DEVFOLIO_API_URL ||
  (typeof process.env.NODE_ENV !== "undefined" &&
  process.env.NODE_ENV === "development"
    ? DEFAULT_DEV
    : DEFAULT_PROD);

/**
 * Waits for Firebase to restore the session on a cold page load, so requests
 * fired during app startup are not sent unauthenticated.
 */
function waitForCurrentUser() {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

async function getIdToken() {
  const user = await waitForCurrentUser();
  if (!user) return "";
  try {
    // Refreshes automatically when the cached token is close to expiry.
    return await user.getIdToken();
  } catch {
    return "";
  }
}

export async function devfolioFetch(path, options = {}) {
  const url = `${DEVFOLIO_API_BASE.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
  const token = await getIdToken();

  if (!token) {
    const err = new Error("Not signed in");
    err.status = 401;
    throw err;
  }

  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!res.ok) {
    const err = new Error(res.statusText || "devfolio API error");
    err.status = res.status;
    throw err;
  }
  return res.json();
}
